import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Chip,
  Grid,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import FilterListIcon from '@mui/icons-material/FilterList';
import MovieGrid from '../components/Movies/MovieGrid';
import Loading from '../components/Common/Loading';
import SpotlightCard from '../components/Common/SpotlightCard';
import api from '../services/api';

const SearchPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [movies, setMovies] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [sortBy, setSortBy] = useState('rating');
  const [order, setOrder] = useState('desc');
  const [minRating, setMinRating] = useState(0);
  const [maxRating, setMaxRating] = useState(10);
  const [yearRange, setYearRange] = useState([1900, new Date().getFullYear()]);
  const [selectedGenres, setSelectedGenres] = useState([]);

  const genres = ['Action', 'Drama', 'Crime', 'Thriller', 'Comedy', 'Sci-Fi', 'Romance', 'Horror', 'Adventure', 'Animation'];

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSearched(true);

      let endpoint = '/movies/sorted';
      let params = new URLSearchParams({
        sortBy,
        order,
        limit: 12,
        page: 1,
      });

      // If search term exists, use search endpoint
      if (searchTerm.trim()) {
        endpoint = '/movies/search';
        params.append('q', searchTerm);
      }

      const response = await api.get(`${endpoint}?${params.toString()}`);
      let filteredMovies = response.data.data;

      // Apply client-side filters
      filteredMovies = filteredMovies.filter(movie => {
        const movieRating = movie.rating;
        const movieYear = new Date(movie.releaseDate).getFullYear();
        
        // Rating filter
        if (movieRating < minRating || movieRating > maxRating) return false;
        
        // Year filter
        if (movieYear < yearRange[0] || movieYear > yearRange[1]) return false;
        
        // Genre filter
        if (selectedGenres.length > 0) {
          const hasGenre = selectedGenres.some(genre => 
            movie.genre?.includes(genre)
          );
          if (!hasGenre) return false;
        }

        return true;
      });

      setMovies(filteredMovies);
      setPagination({
        ...response.data.pagination,
        total: filteredMovies.length,
      });
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Search failed');
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setMovies([]);
    setPagination({});
    setSearched(false);
    setError('');
    setSortBy('rating');
    setOrder('desc');
    setMinRating(0);
    setMaxRating(10);
    setYearRange([1900, new Date().getFullYear()]);
    setSelectedGenres([]);
  };

  const handleGenreToggle = (genre) => {
    setSelectedGenres(prev => 
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handlePageChange = (event, value) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box sx={{ minHeight: '90vh', pt: 12 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h2"
            gutterBottom
            fontWeight={800}
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #a1a1aa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Search Movies
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Find your next favorite film by title or description
          </Typography>

          {/* Search Bar */}
          <Paper
            component="form"
            onSubmit={handleSearch}
            elevation={0}
            sx={{
              p: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              maxWidth: 700,
              mx: 'auto',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                border: '2px solid rgba(99, 102, 241, 0.3)',
              },
              '&:focus-within': {
                border: '2px solid rgba(99, 102, 241, 0.6)',
                boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.1)',
              },
            }}
          >
            <IconButton type="submit" sx={{ p: '12px', color: '#6366f1' }}>
              <SearchIcon fontSize="large" />
            </IconButton>
            <InputBase
              sx={{
                ml: 1,
                flex: 1,
                fontSize: '1.1rem',
                color: 'white',
                '& input::placeholder': {
                  opacity: 0.7,
                },
              }}
              placeholder="Search for movies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <IconButton onClick={handleClear} sx={{ p: '10px' }}>
                <CloseIcon />
              </IconButton>
            )}
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                p: '10px',
                ml: 1,
                color: showFilters ? '#6366f1' : 'inherit',
                background: showFilters ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
              }}
            >
              <TuneIcon />
            </IconButton>
          </Paper>

          {/* Advanced Filters */}
          <Collapse in={showFilters}>
            <SpotlightCard
              className="spotlight-card"
              spotlightColor="rgba(99, 102, 241, 0.2)"
              style={{
                maxWidth: '900px',
                margin: '2rem auto 0',
                padding: '2rem',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <FilterListIcon sx={{ color: '#6366f1' }} />
                <Typography variant="h6" fontWeight={700}>
                  Advanced Filters
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Sort Options */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label="Sort By"
                    >
                      <MenuItem value="rating">⭐ Rating</MenuItem>
                      <MenuItem value="title">🔤 Title</MenuItem>
                      <MenuItem value="releaseDate">📅 Release Date</MenuItem>
                      <MenuItem value="duration">⏱️ Duration</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Order</InputLabel>
                    <Select
                      value={order}
                      onChange={(e) => setOrder(e.target.value)}
                      label="Order"
                    >
                      <MenuItem value="desc">↓ Descending (High to Low)</MenuItem>
                      <MenuItem value="asc">↑ Ascending (Low to High)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Rating Filter */}
                <Grid item xs={12}>
                  <Typography gutterBottom fontWeight={600}>
                    Rating: {minRating} - {maxRating}
                  </Typography>
                  <Slider
                    value={[minRating, maxRating]}
                    onChange={(e, newValue) => {
                      setMinRating(newValue[0]);
                      setMaxRating(newValue[1]);
                    }}
                    valueLabelDisplay="auto"
                    min={0}
                    max={10}
                    step={0.1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                    ]}
                    sx={{
                      color: '#6366f1',
                      '& .MuiSlider-thumb': {
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: '0 0 0 8px rgba(99, 102, 241, 0.16)',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Year Filter */}
                <Grid item xs={12}>
                  <Typography gutterBottom fontWeight={600}>
                    Release Year: {yearRange[0]} - {yearRange[1]}
                  </Typography>
                  <Slider
                    value={yearRange}
                    onChange={(e, newValue) => setYearRange(newValue)}
                    valueLabelDisplay="auto"
                    min={1900}
                    max={new Date().getFullYear()}
                    marks={[
                      { value: 1900, label: '1900' },
                      { value: 1950, label: '1950' },
                      { value: 2000, label: '2000' },
                      { value: new Date().getFullYear(), label: 'Now' },
                    ]}
                    sx={{
                      color: '#ec4899',
                      '& .MuiSlider-thumb': {
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: '0 0 0 8px rgba(236, 72, 153, 0.16)',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Genre Filter */}
                <Grid item xs={12}>
                  <Typography gutterBottom fontWeight={600}>
                    Genres
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {genres.map(genre => (
                      <Chip
                        key={genre}
                        label={genre}
                        onClick={() => handleGenreToggle(genre)}
                        sx={{
                          background: selectedGenres.includes(genre)
                            ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                            : 'rgba(255, 255, 255, 0.05)',
                          border: selectedGenres.includes(genre)
                            ? '1px solid #6366f1'
                            : '1px solid rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          fontWeight: selectedGenres.includes(genre) ? 600 : 400,
                          '&:hover': {
                            background: selectedGenres.includes(genre)
                              ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                              : 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>

                {/* Apply Filters Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <SpotlightCard
                      as="button"
                      className="spotlight-button"
                      spotlightColor="rgba(99, 102, 241, 0.5)"
                      onClick={handleSearch}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.5)',
                        color: 'white',
                        fontSize: '1rem',
                        fontWeight: 600,
                        padding: '12px',
                      }}
                    >
                      <SearchIcon />
                      Apply Filters
                    </SpotlightCard>
                    <SpotlightCard
                      as="button"
                      className="spotlight-button"
                      spotlightColor="rgba(239, 68, 68, 0.3)"
                      onClick={handleClear}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#ef4444',
                        fontSize: '1rem',
                        fontWeight: 600,
                        padding: '12px 24px',
                      }}
                    >
                      Clear All
                    </SpotlightCard>
                  </Box>
                </Grid>
              </Grid>
            </SpotlightCard>
          </Collapse>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              maxWidth: 700,
              mx: 'auto',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading message="Searching movies..." />
        ) : searched && movies.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No movies found matching your criteria
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your filters or search terms
            </Typography>
          </Box>
        ) : movies.length > 0 ? (
          <>
            <Typography variant="h5" sx={{ mb: 4, px: 2 }}>
              Found {movies.length} result{movies.length !== 1 ? 's' : ''}
              {searchTerm && ` for "${searchTerm}"`}
            </Typography>
            <MovieGrid
              movies={movies}
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 12 }}>
            <SearchIcon sx={{ fontSize: 100, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Start searching for your favorite movies
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter a movie title or use advanced filters to find exactly what you're looking for
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SearchPage;
